import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Paper,
  Container,
  Typography,
  IconButton,
  CircularProgress,
  Chip,
  Avatar,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  ToggleButtonGroup,
  ToggleButton,
  Collapse,
  FormControlLabel,
  Switch,
  Checkbox,
  Tabs,
  Tab,
  Divider,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import {
  Edit,
  Close,
  DeleteOutline,
  PhotoCamera,
  SmartToy,
  Add,
  ArrowUpward,
  ArrowDownward,
  AltRoute,
  ExpandLess,
  ExpandMore,
  Shield,
} from "@mui/icons-material";
import HeaderInternal from "../components/Header/HeaderInternal";
import { FooterInternal } from "../components/Footer";
import { FaqButton } from "../components/FaqButton";
import AddButton from "../components/AddButton";
import ResultCount from "../components/ResultCount";
import ClearFiltersButton from "../components/ClearFiltersButton";
import StandardPagination, { ITENS_POR_PAGINA_PADRAO } from "../components/Pagination/StandardPagination";
import ConfirmModal from "../components/modals/ConfirmModal";
import { Toast } from "../components/Toast";
import SavingOverlay from "../components/SavingOverlay";
import { useToast } from "../hooks/useToast";
import { useRouter } from "../contexts/RouterContext";
import { useAuth } from "../contexts/AuthContext";
import { colors, typography, pageHeader } from "../theme/designSystem";

import AgentToolsPicker from "../components/aiAgents/AgentToolsPicker";
import { aiAgentsService, AiAgent, AgentUser, AgentTool, EntityProfile, SafetyPolicy } from "../services/aiAgentsService";
import { aiIntegrationService, AiIntegrationOption, modelLabel } from "../services/aiIntegrationService";

const EFFORTS = [
  { value: "low", label: "Baixo" },
  { value: "medium", label: "Médio" },
  { value: "high", label: "Alto" },
];

const TONE_PRESETS = ["Acolhedor", "Formal", "Objetivo", "Direto"];

const MAX_STAGES = 20;

// Estágio no formulário: strings (o payload converte vazio em null; nome vazio é bloqueado no save).
interface StageForm {
  name: string;
  objective: string;
  instruction: string;
  advanceCondition: string;
}

const blankStage = (): StageForm => ({ name: "", objective: "", instruction: "", advanceCondition: "" });

interface FormState {
  id: string | null; // null = criação
  displayName: string;
  personalityTone: string;
  systemPrompt: string;
  greetingMessage: string;
  fallbackMessage: string;
  maxRepliesPerConversation: string;
  resumeWindowHours: string;
  internalDescription: string;
  customizeProvider: boolean;
  integrationId: string;
  model: string;
  reasoningEffort: string;
  targetProfileId: string; // "" = nenhum
  dataScope: string; // "own" | "entity"
  allowsClinicalData: boolean;
  showInChat: boolean;
  enabledTools: string[];
  /** Tipo em que a aba Ferramentas abre (o da área que usa o agente). */
  toolKind: string | null;
  knowledgeText: string;
  stages: StageForm[];
}

const blankForm = (): FormState => ({
  id: null,
  displayName: "",
  personalityTone: "",
  systemPrompt: "",
  greetingMessage: "",
  fallbackMessage: "",
  maxRepliesPerConversation: "",
  resumeWindowHours: "",
  internalDescription: "",
  customizeProvider: false,
  integrationId: "",
  model: "",
  reasoningEffort: "medium",
  targetProfileId: "",
  dataScope: "own",
  allowsClinicalData: false,
  showInChat: true,
  enabledTools: [],
  toolKind: null,
  knowledgeText: "",
  stages: [],
});


const initials = (name: string) =>
  name.trim().split(/\s+/).slice(0, 2).map((p) => p[0]?.toUpperCase() ?? "").join("") || "IA";

const AiAgents: React.FC = () => {
  const { navigateTo } = useRouter();
  const { user } = useAuth();
  const { toast, showToast, hideToast } = useToast();
  const entityId = user?.entityId || "";
  const theme = useTheme();
  const fullScreenDialog = useMediaQuery(theme.breakpoints.down("sm"));

  const [agents, setAgents] = useState<AiAgent[]>([]);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState<"todos" | "ativos" | "inativos">("todos");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(ITENS_POR_PAGINA_PADRAO);
  const [options, setOptions] = useState<AiIntegrationOption[]>([]);
  const [profiles, setProfiles] = useState<EntityProfile[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [form, setForm] = useState<FormState | null>(null);
  const [tab, setTab] = useState(0); // 0 Comportamento, 1 Roteiro, 2 Conhecimento, 3 Acesso, 4 Ferramentas, 5 Modelo
  // Política de segurança da plataforma (D-OP-25): só leitura, vale para todo agente
  const [policy, setPolicy] = useState<SafetyPolicy | null>(null);
  const [policyOpen, setPolicyOpen] = useState(false);
  const [agentUsers, setAgentUsers] = useState<AgentUser[] | null>(null); // null = ainda não carregado
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [agentTools, setAgentTools] = useState<AgentTool[] | null>(null); // null = ainda não carregado
  const [loadingTools, setLoadingTools] = useState(false);
  const [avatarFile, setAvatarFile] = useState<File | null>(null);
  const [avatarPreview, setAvatarPreview] = useState<string | null>(null);
  const [removeAvatarFlag, setRemoveAvatarFlag] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<AiAgent | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const load = async () => {
    if (!entityId) {
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const [ags, opts, profs, pol] = await Promise.all([
        aiAgentsService.getAgents(entityId, true),
        aiIntegrationService.getOptions(entityId).catch(() => [] as AiIntegrationOption[]),
        aiAgentsService.getProfiles().catch(() => [] as EntityProfile[]),
        aiAgentsService.getSafetyPolicy().catch(() => null),
      ]);
      setAgents(ags);
      // Agente recém-criado: o servidor ligou as ferramentas; o formulário aberto passa a refletir isso
      // (senão um segundo Salvar gravaria a lista vazia)
      setForm((prev) => {
        const saved = prev?.id ? ags.find((a) => a.id === prev.id) : undefined;
        return prev && saved && prev.enabledTools.length === 0
          ? { ...prev, enabledTools: saved.enabledTools ?? [] }
          : prev;
      });
      setOptions(opts);
      setProfiles(profs);
      setPolicy(pol);
    } catch {
      showToast("Erro ao carregar os agentes de IA.", "error");
    } finally {
      setLoading(false);
    }
  };

  /** Minusculas e sem acento: quem busca "thalia" espera achar "Thália". */
  const normalize = (v: string) =>
    v.toLowerCase().normalize("NFD").replace(/[̀-ͯ]/g, "");

  const filtered = agents.filter((a) => {
    if (statusFilter !== "todos" && a.active !== (statusFilter === "ativos")) {
      return false;
    }
    if (!search.trim()) {
      return true;
    }
    const alvo = normalize(
      [a.displayName, a.internalDescription, a.personalityTone, a.model]
        .filter(Boolean)
        .join(" "),
    );
    return alvo.includes(normalize(search));
  });

  const totalPages = Math.max(1, Math.ceil(filtered.length / itemsPerPage));
  const paged = filtered.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);

  // Mexeu no filtro, volta para a primeira pagina: senao a lista aparece vazia sem motivo
  useEffect(() => {
    setCurrentPage(1);
  }, [search, statusFilter]);

  const clearFilters = () => {
    setSearch("");
    setStatusFilter("todos");
  };

  const hasFilterChanges = search !== "" || statusFilter !== "todos";

  useEffect(() => {
    document.title = "Clinic4Us - Agentes de IA";
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Revoga o object URL do preview ao trocar/fechar (evita vazamento de memória).
  useEffect(() => () => {
    if (avatarPreview) URL.revokeObjectURL(avatarPreview);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [avatarPreview]);

  const selectedProvider = useMemo(
    () => options.find((o) => o.id === form?.integrationId),
    [options, form?.integrationId]
  );

  const resetAvatarState = () => {
    setAvatarFile(null);
    setAvatarPreview((prev) => {
      if (prev) URL.revokeObjectURL(prev);
      return null;
    });
    setRemoveAvatarFlag(false);
  };

  const openCreate = () => {
    resetAvatarState();
    setTab(0);
    setAgentUsers(null);
    setAgentTools(null);
    const base = blankForm();
    const first = options[0];
    if (first) {
      base.integrationId = first.id;
      base.model = first.defaultModel;
    }
    setForm(base);
  };

  const openEdit = async (agent: AiAgent) => {
    resetAvatarState();
    setTab(0);
    setAgentUsers(null);
    setAgentTools(null);
    setForm({
      id: agent.id,
      displayName: agent.displayName,
      personalityTone: agent.personalityTone || "",
      systemPrompt: agent.systemPrompt,
      greetingMessage: agent.greetingMessage || "",
      fallbackMessage: agent.fallbackMessage || "",
      maxRepliesPerConversation:
        agent.maxRepliesPerConversation != null ? String(agent.maxRepliesPerConversation) : "",
      resumeWindowHours:
        agent.resumeWindowHours != null ? String(agent.resumeWindowHours) : "",
      internalDescription: agent.internalDescription || "",
      customizeProvider: !!agent.integrationId,
      integrationId: agent.integrationId || options[0]?.id || "",
      model: agent.model || options.find((o) => o.id === agent.integrationId)?.defaultModel || options[0]?.defaultModel || "",
      reasoningEffort: agent.reasoningEffort || "medium",
      targetProfileId: agent.targetProfileId || "",
      dataScope: agent.dataScope || "own",
      allowsClinicalData: agent.allowsClinicalData ?? false,
      showInChat: agent.showInChat ?? true,
      enabledTools: agent.enabledTools ?? [],
      toolKind: agent.areaKeys?.length === 1 ? agent.areaKeys[0] : null,
      knowledgeText: agent.knowledgeText || "",
      stages: (agent.stages ?? []).map((s) => ({
        name: s.name,
        objective: s.objective ?? "",
        instruction: s.instruction ?? "",
        advanceCondition: s.advanceCondition ?? "",
      })),
    });
    if (agent.hasAvatar) {
      try {
        const url = await aiAgentsService.getAvatar(agent.id);
        setAvatarPreview(url);
      } catch {
        // sem preview; segue sem quebrar o formulário.
      }
    }
  };

  const closeForm = () => {
    setForm(null);
    resetAvatarState();
    setAgentUsers(null);
    setAgentTools(null);
  };

  // Lazy-load ao entrar nas abas Acesso(3)/Ferramentas(4) (só em edição — precisa do id do agente).
  const changeTab = (next: number) => {
    setTab(next);
    if (next === 3 && form?.id && agentUsers === null && !loadingUsers) {
      setLoadingUsers(true);
      aiAgentsService
        .getUsers(form.id)
        .then(setAgentUsers)
        .catch(() => showToast("Erro ao carregar os usuários.", "error"))
        .finally(() => setLoadingUsers(false));
    }
    if (next === 4 && form?.id && agentTools === null && !loadingTools) {
      setLoadingTools(true);
      aiAgentsService
        .getTools(form.id, form.targetProfileId || undefined)
        .then(setAgentTools)
        .catch(() => showToast("Erro ao carregar as ferramentas.", "error"))
        .finally(() => setLoadingTools(false));
    }
  };

  // Ao trocar o perfil-alvo, recarrega a lista de ferramentas (disponibilidade muda com o perfil).
  // A herança é automática: a lista é read-only, apenas informa o que o perfil acessa.
  const changeTargetProfile = (targetProfileId: string) => {
    patch({ targetProfileId });
    if (form?.id && agentTools !== null) {
      aiAgentsService
        .getTools(form.id, targetProfileId || undefined)
        .then(setAgentTools)
        .catch(() => {});
    }
  };

  const toggleUser = (userId: string) =>
    setAgentUsers((prev) =>
      prev ? prev.map((u) => (u.userId === userId ? { ...u, assigned: !u.assigned } : u)) : prev);

  const patch = (changes: Partial<FormState>) =>
    setForm((prev) => (prev ? { ...prev, ...changes } : prev));

  const changeProvider = (id: string) => {
    const provider = options.find((o) => o.id === id);
    patch({ integrationId: id, model: provider && !provider.models.includes(form?.model || "") ? provider.defaultModel : form?.model || "" });
  };

  // --- Roteiro (estágios) ---
  const patchStage = (idx: number, changes: Partial<StageForm>) =>
    setForm((prev) =>
      prev ? { ...prev, stages: prev.stages.map((s, i) => (i === idx ? { ...s, ...changes } : s)) } : prev);

  const addStage = () =>
    setForm((prev) => (prev && prev.stages.length < MAX_STAGES ? { ...prev, stages: [...prev.stages, blankStage()] } : prev));

  const removeStage = (idx: number) =>
    setForm((prev) => (prev ? { ...prev, stages: prev.stages.filter((_, i) => i !== idx) } : prev));

  const moveStage = (idx: number, dir: -1 | 1) =>
    setForm((prev) => {
      if (!prev) return prev;
      const target = idx + dir;
      if (target < 0 || target >= prev.stages.length) return prev;
      const next = [...prev.stages];
      [next[idx], next[target]] = [next[target], next[idx]];
      return { ...prev, stages: next };
    });

  const pickAvatar = (file: File | null) => {
    if (!file) return;
    setAvatarFile(file);
    setRemoveAvatarFlag(false);
    setAvatarPreview((prev) => {
      if (prev) URL.revokeObjectURL(prev);
      return URL.createObjectURL(file);
    });
  };

  const clearAvatar = () => {
    setAvatarFile(null);
    setRemoveAvatarFlag(true);
    setAvatarPreview((prev) => {
      if (prev) URL.revokeObjectURL(prev);
      return null;
    });
  };

  const save = async () => {
    if (!form) return;
    if (!form.displayName.trim()) {
      showToast("Informe o nome de exibição do agente.", "error");
      return;
    }
    if (!form.systemPrompt.trim()) {
      showToast("Informe as instruções (system prompt) do agente.", "error");
      return;
    }
    if (form.stages.some((s) => !s.name.trim())) {
      setTab(1);
      showToast("Informe o nome de cada estágio do roteiro (ou remova o estágio vazio).", "error");
      return;
    }
    setSaving(true);
    try {
      const input = {
        displayName: form.displayName.trim(),
        internalDescription: form.internalDescription.trim() || null,
        personalityTone: form.personalityTone.trim() || null,
        systemPrompt: form.systemPrompt.trim(),
        greetingMessage: form.greetingMessage.trim() || null,
        fallbackMessage: form.fallbackMessage.trim() || null,
        maxRepliesPerConversation: Number(form.maxRepliesPerConversation) || null,
        resumeWindowHours: Number(form.resumeWindowHours) || null,
        integrationId: form.customizeProvider ? form.integrationId || null : null,
        model: form.customizeProvider ? form.model || null : null,
        reasoningEffort: form.customizeProvider ? form.reasoningEffort : null,
        targetProfileId: form.targetProfileId || null,
        dataScope: form.dataScope || "own",
        allowsClinicalData: form.allowsClinicalData ?? false,
        showInChat: form.showInChat,
        // Criar sem lista: o servidor liga todas as que existem hoje (a aba só abre depois de salvar)
        enabledTools: form.id ? form.enabledTools : null,
        knowledgeText: form.knowledgeText.trim() || null,
        stages: form.stages.map((s) => ({
          name: s.name.trim(),
          objective: s.objective.trim() || null,
          instruction: s.instruction.trim() || null,
          advanceCondition: s.advanceCondition.trim() || null,
        })),
      };

      let id: string;
      const wasCreate = !form.id;
      if (form.id) {
        await aiAgentsService.update(form.id, input);
        id = form.id;
      } else {
        id = await aiAgentsService.create(entityId, input);
      }

      if (avatarFile) {
        await aiAgentsService.setAvatar(id, avatarFile);
      } else if (removeAvatarFlag && form.id) {
        await aiAgentsService.removeAvatar(id);
      }

      // Atribuição (aba Acesso) — só se foi carregada nesta edição.
      if (agentUsers !== null) {
        await aiAgentsService.setUsers(id, agentUsers.filter((u) => u.assigned).map((u) => u.userId));
      }

      showToast(wasCreate ? "Agente criado." : "Agente atualizado.", "success");
      // Mantém o modal aberto (pedido do Hilton) para seguir configurando o roteiro. Ao criar,
      // vira modo edição (senão um segundo Salvar criaria duplicado) e o avatar já consumido é limpo.
      setForm((prev) => (prev ? { ...prev, id } : prev));
      setAvatarFile(null);
      setRemoveAvatarFlag(false);
      await load();
    } catch (e: unknown) {
      const message =
        (e as { response?: { data?: { description?: string } } })?.response?.data?.description ||
        "Erro ao salvar o agente.";
      showToast(message, "error");
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async (agent: AiAgent) => {
    try {
      await aiAgentsService.setActive(agent.id, !agent.active);
      await load();
    } catch {
      showToast("Erro ao alterar a disponibilidade do agente.", "error");
    }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    setSaving(true);
    try {
      await aiAgentsService.remove(deleteTarget.id);
      showToast("Agente excluído.", "success");
      setDeleteTarget(null);
      await load();
    } catch {
      showToast("Erro ao excluir o agente.", "error");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", minHeight: "100vh" }}>
      <HeaderInternal
        showCTAButton={false}
        className="login-header"
        isLoggedIn={true}
        userEmail={user?.email || ""}
        userProfile={user?.role || ""}
        clinicName={user?.clinicName || "Clínica"}
        notificationCount={0}
        onRevalidateLogin={() => {}}
        onNotificationClick={() => {}}
        onUserClick={() => {}}
        onLogoClick={() => navigateTo("dashboard")}
      />

      <Box component="main" sx={{ padding: "1rem", minHeight: "calc(100vh - 120px)", background: colors.background, marginTop: "85px", flex: 1 }}>
        <Container maxWidth={false} disableGutters sx={pageHeader.container}>
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", mb: 1, gap: 2, flexWrap: "nowrap" }}>
            <Box sx={{ minWidth: 0, flex: "1 1 auto" }}>
              <Typography variant="h4" className="c4u-page-header__title">Agentes de IA
              </Typography>
              <Typography variant="body2" className="c4u-page-header__subtitle">Cadastro dos assistentes de IA da entidade: nome, personalidade e instruções. O provedor de IA vem da Configuração de IA (com override opcional por agente).
              </Typography>
            </Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexShrink: 0, justifyContent: "flex-end" }}>
              <FaqButton />
            </Box>
          </Box>

          {loading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
              <CircularProgress sx={{ color: colors.primary }} />
            </Box>
          ) : agents.length === 0 ? (
            <Box sx={{ textAlign: "center", py: 6, color: colors.textSecondary }}>
              <SmartToy sx={{ fontSize: 48, color: "#ced4da", mb: 1 }} />
              <Typography variant="body1" sx={{ mb: 0.5, color: colors.textPrimary }}>Nenhum agente cadastrado</Typography>
              <Typography variant="body2">Crie o primeiro assistente da entidade em "Novo agente".</Typography>
            </Box>
          ) : (
            <div className="c4u-panel">
              <div className="c4u-filters__row">
                <div className="c4u-filters__fields">
                  <TextField
                    size="small"
                    label="Busca por palavra"
                    placeholder="Nome, descrição, tom ou modelo"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                    fullWidth
                    inputProps={{ maxLength: 120 }}
                  />
                  <TextField
                    select
                    size="small"
                    label="Situação"
                    value={statusFilter}
                    onChange={(e) =>
                      setStatusFilter(e.target.value as "todos" | "ativos" | "inativos")
                    }
                    InputLabelProps={{ shrink: true }}
                    fullWidth
                  >
                    <MenuItem value="todos">Todos</MenuItem>
                    <MenuItem value="ativos">Ativos</MenuItem>
                    <MenuItem value="inativos">Inativos</MenuItem>
                  </TextField>
                </div>

                <div className="c4u-filters__actions">
                  <ClearFiltersButton onClick={clearFilters} disabled={!hasFilterChanges} />
                  <AddButton onClick={openCreate} title="Novo agente" />
                </div>
              </div>

              <ResultCount shown={filtered.length} total={agents.length} />
              {/* Cabeçalho: toda lista tem um, e cada dado fica na sua coluna */}
              <Box className="c4u-table__header">
                <Box className="admin-plans-header-cell" sx={{ flex: "1 1 240px", minWidth: 0 }}>Agente</Box>
                <Box className="admin-plans-header-cell" sx={{ flex: "0 0 90px" }}>Situação</Box>
                <Box className="admin-plans-header-cell" sx={{ flex: "0 0 90px" }}>Ações</Box>
              </Box>

              {paged.map((agent) => (
                <Box
                  key={agent.id}
                  className="c4u-table__row"
                >
                  {/* Avatar + nome + provedor */}
                  <Box className="admin-plans-cell" data-label="Agente" sx={{ display: "flex", alignItems: "center", gap: 1.5, flex: "1 1 240px", minWidth: 0 }}>
                    <Avatar sx={{ width: 40, height: 40, bgcolor: colors.primary, fontSize: "0.9rem", flexShrink: 0 }}>
                      {initials(agent.displayName)}
                    </Avatar>
                    <Box sx={{ minWidth: 0, flex: "1 1 auto" }}>
                      <Typography sx={{ fontWeight: 600, color: colors.textPrimary, wordBreak: "break-word", lineHeight: 1.25 }}>
                        {agent.displayName}
                      </Typography>
                      <Typography variant="caption" sx={{ color: colors.textSecondary }}>
                        {agent.integrationId ? "Provedor personalizado" : "Provedor herdado da entidade"}
                      </Typography>
                    </Box>
                  </Box>

                  {/* Status + ações */}
                  <Box className="admin-plans-cell" data-label="Situação" sx={{ flex: "0 0 90px" }}>
                    <Chip
                      label={agent.active ? "Ativo" : "Inativo"}
                      size="small"
                      onClick={(e) => { e.stopPropagation(); toggleActive(agent); }}
                      title={agent.active ? "Clique para inativar" : "Clique para ativar"}
                      sx={{ color: "white", fontWeight: 600, cursor: "pointer", backgroundColor: agent.active ? "#28a745" : "#adb5bd" }}
                    />
                  </Box>
                  <Box className="admin-plans-cell admin-plans-actions" data-label="Ações" sx={{ flex: "0 0 90px", display: "flex", gap: 0.5, justifyContent: "flex-end" }}>
                      <IconButton size="small" className="c4u-row-action" onClick={(e) => { e.stopPropagation(); openEdit(agent); }} title="Editar" sx={{ backgroundColor: "#03B4C6", color: "white", width: 32, height: 32, "&:hover": { backgroundColor: "#029AAB" } }}>
                        <Edit sx={{ fontSize: "1rem" }} />
                      </IconButton>
                      <IconButton size="small" className="c4u-row-action" onClick={(e) => { e.stopPropagation(); setDeleteTarget(agent); }} title="Excluir" sx={{ backgroundColor: "#dc3545", color: "white", width: 32, height: 32, "&:hover": { backgroundColor: "#c82333" } }}>
                        <DeleteOutline sx={{ fontSize: "1rem" }} />
                      </IconButton>
                  </Box>
                </Box>
              ))}

              {filtered.length > 0 && (
                <StandardPagination
                  currentPage={currentPage}
                  totalPages={totalPages}
                  itemsPerPage={itemsPerPage}
                  totalItems={filtered.length}
                  onPageChange={setCurrentPage}
                  onItemsPerPageChange={(n) => {
                    setItemsPerPage(n);
                    setCurrentPage(1);
                  }}
                />
              )}
            </div>
          )}
        </Container>
      </Box>

      {/* Formulário (criar/editar) */}
      <Dialog open={form !== null} onClose={closeForm} maxWidth="md" fullWidth fullScreen={fullScreenDialog} PaperProps={{ sx: { borderRadius: fullScreenDialog ? 0 : "12px" } }}>
        <DialogTitle sx={{ backgroundColor: colors.primary, color: colors.white, display: "flex", justifyContent: "space-between", alignItems: "center", padding: "1rem 1.25rem" }}>
          <Typography component="h3" sx={{ fontSize: "1.1rem", fontWeight: typography.fontWeight.semibold, margin: 0 }}>
            {form?.id ? "Editar agente" : "Novo agente"}
          </Typography>
          <IconButton onClick={closeForm} sx={{ color: colors.white, padding: "0.25rem" }}><Close /></IconButton>
        </DialogTitle>
        <DialogContent sx={{ padding: "1.25rem !important", paddingTop: "1.5rem !important", position: "relative" }}>
          <SavingOverlay open={saving} message="Salvando agente..." />
          {form && (
            <>
              <Tabs
                value={tab}
                onChange={(_, v) => changeTab(v)}
                variant="scrollable"
                scrollButtons="auto"
                sx={{ mb: 2, borderBottom: "1px solid #e9ecef", minHeight: 40, "& .MuiTab-root": { minHeight: 40 } }}
              >
                <Tab label="Comportamento" sx={{ textTransform: "none", fontWeight: 600 }} />
                <Tab label="Roteiro" sx={{ textTransform: "none", fontWeight: 600 }} />
                <Tab label="Conhecimento" sx={{ textTransform: "none", fontWeight: 600 }} />
                <Tab label="Acesso" sx={{ textTransform: "none", fontWeight: 600 }} />
                <Tab label="Ferramentas" sx={{ textTransform: "none", fontWeight: 600 }} />
                <Tab label="Modelo" sx={{ textTransform: "none", fontWeight: 600 }} />
              </Tabs>

              <Box sx={{ display: tab === 0 ? "flex" : "none", flexDirection: "column", gap: 2 }}>
              {/* Regras da plataforma (D-OP-25): valem antes das instruções abaixo e não são editáveis */}
              {policy && (
                <Box sx={{ border: `1px solid ${colors.primarySoft}`, bgcolor: colors.primaryLight, borderRadius: "8px" }}>
                  <Button
                    onClick={() => setPolicyOpen((o) => !o)}
                    aria-expanded={policyOpen}
                    startIcon={<Shield sx={{ color: colors.primaryText }} />}
                    endIcon={policyOpen ? <ExpandLess /> : <ExpandMore />}
                    sx={{ width: "100%", justifyContent: "flex-start", textTransform: "none", color: colors.textPrimary, fontWeight: 600, px: 1.5, py: 1, "& .MuiButton-endIcon": { ml: "auto" } }}
                  >
                    Regras da plataforma — valem para todo agente e não podem ser editadas
                  </Button>
                  <Collapse in={policyOpen}>
                    <Box sx={{ px: 2, pb: 1.5 }}>
                      <Typography variant="caption" sx={{ display: "block", color: colors.textSecondary, mb: 1 }}>
                        Entram antes das instruções deste agente e têm precedência sobre elas. Além do texto, o
                        sistema impede o agente de fazer o que as regras proíbem. Versão {policy.version}.
                      </Typography>
                      <Box component="ol" sx={{ m: 0, pl: 2.5, display: "flex", flexDirection: "column", gap: 0.5 }}>
                        {policy.rules.map((r) => (
                          <Typography key={r.code} component="li" variant="body2" sx={{ color: colors.textPrimary }}>
                            {r.text}
                            {r.systemOnly && (
                              <Box component="span" sx={{ color: colors.textSecondary }}> (não se aplica ao WhatsApp, que não acessa o sistema)</Box>
                            )}
                          </Typography>
                        ))}
                      </Box>
                      {(policy.byKind.Clinical?.length || policy.byKind.PublicChannel?.length) ? (
                        <Typography variant="caption" sx={{ display: "block", color: colors.textSecondary, mt: 1 }}>
                          Regras adicionais: no Assistente do prontuário — {policy.byKind.Clinical.map((r) => r.text.toLowerCase()).join(" ")} No
                          WhatsApp — {policy.byKind.PublicChannel.map((r) => r.text.toLowerCase()).join(" ")}
                        </Typography>
                      ) : null}
                    </Box>
                  </Collapse>
                </Box>
              )}
              {/* Avatar + nome */}
              <Box sx={{ display: "flex", gap: 2, alignItems: "center", flexWrap: "wrap" }}>
                <Avatar src={avatarPreview || undefined} sx={{ width: 64, height: 64, bgcolor: colors.primary, fontSize: "1.3rem" }}>
                  {!avatarPreview && initials(form.displayName)}
                </Avatar>
                <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
                  <input ref={fileInputRef} type="file" accept="image/jpeg,image/png,image/webp" hidden onChange={(e) => pickAvatar(e.target.files?.[0] ?? null)} />
                  <Box sx={{ display: "flex", gap: 1 }}>
                    <Button size="small" startIcon={<PhotoCamera />} onClick={() => fileInputRef.current?.click()} sx={{ textTransform: "none", color: colors.primary }}>
                      {avatarPreview ? "Trocar avatar" : "Adicionar avatar"}
                    </Button>
                    {avatarPreview && (
                      <Button size="small" onClick={clearAvatar} sx={{ textTransform: "none", color: colors.error }}>Remover</Button>
                    )}
                  </Box>
                  <Typography variant="caption" sx={{ color: colors.textSecondary }}>JPEG, PNG ou WebP, até 5 MB.</Typography>
                </Box>
              </Box>

              <TextField
                label="Nome de exibição"
                value={form.displayName}
                onChange={(e) => patch({ displayName: e.target.value })}
                fullWidth size="small" required inputProps={{ maxLength: 100 }} InputLabelProps={{ shrink: true }}
                placeholder="ex.: Lidi"
              />

              <Box>
                <TextField
                  label="Personalidade / tom"
                  value={form.personalityTone}
                  onChange={(e) => patch({ personalityTone: e.target.value })}
                  fullWidth size="small" inputProps={{ maxLength: 500 }} InputLabelProps={{ shrink: true }}
                  placeholder="ex.: acolhedor e objetivo"
                />
                <Box sx={{ display: "flex", gap: 0.5, mt: 0.5, flexWrap: "wrap" }}>
                  {TONE_PRESETS.map((t) => (
                    <Chip key={t} label={t} size="small" variant="outlined" onClick={() => patch({ personalityTone: t })} sx={{ cursor: "pointer" }} />
                  ))}
                </Box>
              </Box>

              <TextField
                label="Instruções (system prompt)"
                value={form.systemPrompt}
                onChange={(e) => patch({ systemPrompt: e.target.value })}
                fullWidth size="small" required multiline minRows={18} inputProps={{ maxLength: 50000 }} InputLabelProps={{ shrink: true }}
                placeholder="Quem é o agente, o que faz e o que NÃO faz."
                helperText={`O núcleo do comportamento. Quanto mais claro, melhor a resposta.  ${form.systemPrompt.length}/50000`}
              />

              <TextField
                label="Mensagem de saudação"
                value={form.greetingMessage}
                onChange={(e) => patch({ greetingMessage: e.target.value })}
                fullWidth size="small" inputProps={{ maxLength: 1000 }} InputLabelProps={{ shrink: true }}
                placeholder="Primeira fala ao abrir o chat."
              />

              <TextField
                label="Quando não souber"
                value={form.fallbackMessage}
                onChange={(e) => patch({ fallbackMessage: e.target.value })}
                fullWidth size="small" inputProps={{ maxLength: 1000 }} InputLabelProps={{ shrink: true }}
                placeholder="ex.: encaminhar a um humano."
              />

              <TextField
                label="Passar para atendimento humano após N respostas"
                value={form.maxRepliesPerConversation}
                onChange={(e) =>
                  patch({ maxRepliesPerConversation: e.target.value.replace(/\D/g, "").slice(0, 3) })
                }
                fullWidth size="small" inputProps={{ maxLength: 3, inputMode: "numeric" }}
                InputLabelProps={{ shrink: true }}
                placeholder="20"
                helperText="Vale para o atendimento automático no WhatsApp. Atingido o limite, o agente encerra encaminhando a uma pessoa e o lead vira pendência no painel. Em branco usa o padrão do sistema (20)."
              />

              <TextField
                label="Recomeçar a conversa após N horas sem contato"
                value={form.resumeWindowHours}
                onChange={(e) =>
                  patch({ resumeWindowHours: e.target.value.replace(/\D/g, "").slice(0, 3) })
                }
                fullWidth size="small" inputProps={{ maxLength: 3, inputMode: "numeric" }}
                InputLabelProps={{ shrink: true }}
                placeholder="72"
                helperText="Vale para o atendimento no WhatsApp. Passado esse silêncio, o agente não retoma o assunto anterior — acolhe como retorno, mantendo o que já está no cadastro. Em branco usa o padrão do sistema (72h, ou seja, 3 dias)."
              />

              <TextField
                label="Descrição interna (não vai para a IA)"
                value={form.internalDescription}
                onChange={(e) => patch({ internalDescription: e.target.value })}
                fullWidth size="small" inputProps={{ maxLength: 500 }} InputLabelProps={{ shrink: true }}
              />

              </Box>

              {/* Aba Roteiro (estágios) */}
              <Box sx={{ display: tab === 1 ? "flex" : "none", flexDirection: "column", gap: 1.5 }}>
                <Typography variant="body2" sx={{ color: colors.textSecondary }}>
                  Conversa guiada em etapas (ex.: Saudação → Identificação → Objetivo → Encaminhamento). Sem estágios, o agente conversa livremente pelas instruções. O roteiro passa a valer quando o motor de conversa estiver ativo.
                </Typography>

                {form.stages.length === 0 ? (
                  <Box sx={{ textAlign: "center", py: 4, color: colors.textSecondary, border: "1px dashed #ced4da", borderRadius: "8px" }}>
                    <AltRoute sx={{ fontSize: 40, color: "#ced4da", mb: 1 }} />
                    <Typography variant="body2" sx={{ mb: 0.5, color: colors.textPrimary }}>Nenhum estágio — agente livre</Typography>
                    <Typography variant="caption">Adicione estágios para guiar a conversa passo a passo.</Typography>
                  </Box>
                ) : (
                  form.stages.map((s, i) => (
                    <Paper key={i} elevation={0} sx={{ border: "1px solid #e9ecef", borderRadius: "8px", p: 1.5, display: "flex", flexDirection: "column", gap: 1.5 }}>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                        <Chip label={`Etapa ${i + 1}`} size="small" sx={{ bgcolor: colors.primary, color: "white", fontWeight: 600 }} />
                        <Box sx={{ flex: 1 }} />
                        <IconButton size="small" disabled={i === 0} onClick={() => moveStage(i, -1)} title="Subir">
                          <ArrowUpward sx={{ fontSize: "1.1rem" }} />
                        </IconButton>
                        <IconButton size="small" disabled={i === form.stages.length - 1} onClick={() => moveStage(i, 1)} title="Descer">
                          <ArrowDownward sx={{ fontSize: "1.1rem" }} />
                        </IconButton>
                        <IconButton size="small" onClick={() => removeStage(i)} title="Remover estágio" sx={{ color: colors.error }}>
                          <DeleteOutline sx={{ fontSize: "1.1rem" }} />
                        </IconButton>
                      </Box>
                      <TextField
                        label="Nome do estágio" required value={s.name}
                        onChange={(e) => patchStage(i, { name: e.target.value })}
                        fullWidth size="small" inputProps={{ maxLength: 100 }} InputLabelProps={{ shrink: true }}
                        placeholder="ex.: Identificação"
                      />
                      <TextField
                        label="Objetivo" value={s.objective}
                        onChange={(e) => patchStage(i, { objective: e.target.value })}
                        fullWidth size="small" inputProps={{ maxLength: 500 }} InputLabelProps={{ shrink: true }}
                        placeholder="o que o agente busca alcançar aqui"
                      />
                      <TextField
                        label="Instrução" value={s.instruction}
                        onChange={(e) => patchStage(i, { instruction: e.target.value })}
                        fullWidth size="small" multiline minRows={2} inputProps={{ maxLength: 2000 }} InputLabelProps={{ shrink: true }}
                        placeholder="como o agente age neste estágio"
                      />
                      <TextField
                        label="Condição de avanço" value={s.advanceCondition}
                        onChange={(e) => patchStage(i, { advanceCondition: e.target.value })}
                        fullWidth size="small" inputProps={{ maxLength: 500 }} InputLabelProps={{ shrink: true }}
                        placeholder="quando passar ao próximo estágio"
                      />
                    </Paper>
                  ))
                )}

                <Button
                  startIcon={<Add />} onClick={addStage} disabled={form.stages.length >= MAX_STAGES}
                  variant="outlined"
                  sx={{ textTransform: "none", color: colors.primary, borderColor: colors.primary, alignSelf: "flex-start" }}
                >
                  Adicionar estágio
                </Button>
                {form.stages.length >= MAX_STAGES && (
                  <Typography variant="caption" sx={{ color: colors.textSecondary }}>Máximo de {MAX_STAGES} estágios.</Typography>
                )}
              </Box>

              {/* Aba Conhecimento (Pilar 3, Fase 1) */}
              <Box sx={{ display: tab === 2 ? "flex" : "none", flexDirection: "column", gap: 1.5 }}>
                <Typography variant="body2" sx={{ color: colors.textSecondary }}>
                  Cole a base de conhecimento do agente (texto e links) — endereços, horários, valores, regras, FAQ. Vai junto das instruções e o agente usa como referência principal. Aceita markdown.
                </Typography>
                <TextField
                  label="Base de conhecimento (markdown)"
                  value={form.knowledgeText}
                  onChange={(e) => patch({ knowledgeText: e.target.value })}
                  fullWidth size="small" multiline minRows={12} inputProps={{ maxLength: 50000 }} InputLabelProps={{ shrink: true }}
                  placeholder={"## Sobre a clínica\nEndereço: ...\nHorário de atendimento: ...\n\n## Links de referência\n- Site: https://..."}
                  helperText={`${form.knowledgeText.length}/50000  Para bases grandes (muitos documentos), avaliaremos indexação (RAG) numa fase seguinte.`}
                />
              </Box>

              {/* Aba Acesso (atribuição user×agente) */}
              <Box sx={{ display: tab === 3 ? "flex" : "none", flexDirection: "column", gap: 1.5 }}>
                <Typography variant="body2" sx={{ color: colors.textSecondary }}>
                  Quem vê este agente no chat. O acesso aos dados continua limitado ao que cada usuário já pode ver.
                </Typography>
                {!form.id ? (
                  <Box sx={{ textAlign: "center", py: 4, color: colors.textSecondary, border: "1px dashed #ced4da", borderRadius: "8px" }}>
                    <Typography variant="body2">Salve o agente para definir quem tem acesso.</Typography>
                  </Box>
                ) : loadingUsers || agentUsers === null ? (
                  <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
                    <CircularProgress size={24} sx={{ color: colors.primary }} />
                  </Box>
                ) : (
                  <>
                    {/* Dropdown para adicionar — só os que ainda não estão na lista */}
                    <FormControl size="small" fullWidth>
                      <InputLabel>Adicionar profissional</InputLabel>
                      <Select
                        label="Adicionar profissional"
                        value=""
                        onChange={(e) => e.target.value && toggleUser(e.target.value as string)}
                      >
                        {agentUsers.filter((u) => !u.assigned).length === 0 ? (
                          <MenuItem value="" disabled>Todos os profissionais já estão na lista</MenuItem>
                        ) : (
                          agentUsers
                            .filter((u) => !u.assigned)
                            .map((u) => (
                              <MenuItem key={u.userId} value={u.userId}>{u.name || u.userId}</MenuItem>
                            ))
                        )}
                      </Select>
                    </FormControl>

                    {/* Lista dos que têm acesso */}
                    {agentUsers.filter((u) => u.assigned).length === 0 ? (
                      <Typography variant="body2" sx={{ color: colors.textSecondary, py: 2, textAlign: "center" }}>
                        Nenhum profissional com acesso ainda. Adicione acima.
                      </Typography>
                    ) : (
                      <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
                        {agentUsers
                          .filter((u) => u.assigned)
                          .map((u) => (
                            <Box
                              key={u.userId}
                              sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1, p: 1, border: "1px solid #e9ecef", borderRadius: "8px" }}
                            >
                              <Typography sx={{ color: colors.textPrimary, wordBreak: "break-word" }}>
                                {u.name || u.userId}
                              </Typography>
                              <IconButton
                                size="small"
                                onClick={() => toggleUser(u.userId)}
                                title="Remover acesso"
                                sx={{ color: colors.error, flexShrink: 0 }}
                              >
                                <DeleteOutline sx={{ fontSize: "1.1rem" }} />
                              </IconButton>
                            </Box>
                          ))}
                      </Box>
                    )}
                  </>
                )}
              </Box>

              {/* Aba Ferramentas (Pilar 4) */}
              <Box sx={{ display: tab === 4 ? "flex" : "none", flexDirection: "column", gap: 1.5 }}>
                <Typography variant="body2" sx={{ color: colors.textSecondary }}>
                  Defina o público (perfil), o escopo dos dados e as ferramentas que o agente usa.
                </Typography>

                <FormControl size="small" fullWidth>
                  <InputLabel>Perfil-alvo (público)</InputLabel>
                  <Select
                    label="Perfil-alvo (público)"
                    value={form.targetProfileId}
                    onChange={(e) => changeTargetProfile(e.target.value)}
                  >
                    <MenuItem value="">Qualquer perfil</MenuItem>
                    {profiles.map((p) => (<MenuItem key={p.id} value={p.id}>{p.name}</MenuItem>))}
                  </Select>
                </FormControl>

                <Box>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={form.showInChat}
                        onChange={(e) => patch({ showInChat: e.target.checked })}
                        sx={{ "& .Mui-checked": { color: colors.primary } }}
                      />
                    }
                    label={<Typography sx={{ fontSize: 14, fontWeight: 600 }}>Aparece no chat</Typography>}
                  />
                  <Typography variant="caption" sx={{ display: "block", color: colors.textSecondary }}>
                    Desligado, o agente some da lista de conversas do chat e só atende na área que o usa
                    na página Assistentes. O agente escolhido para o Assistente do prontuário nunca
                    aparece no chat, com esta opção ligada ou não.
                  </Typography>
                </Box>

                <Box>
                  <Typography variant="caption" sx={{ display: "block", color: colors.textSecondary, mb: 0.5 }}>
                    Escopo dos dados
                  </Typography>
                  <ToggleButtonGroup
                    exclusive
                    size="small"
                    value={form.dataScope}
                    onChange={(_, v) => v && patch({ dataScope: v })}
                    sx={{ flexWrap: "wrap" }}
                  >
                    <ToggleButton value="own" sx={{ textTransform: "none" }}>Próprio profissional</ToggleButton>
                    <ToggleButton value="entity" sx={{ textTransform: "none" }}>Toda a entidade</ToggleButton>
                  </ToggleButtonGroup>
                </Box>

                <Typography variant="caption" sx={{ color: colors.textSecondary }}>
                  As ferramentas abaixo dependem do perfil-alvo (só o que aquele perfil acessa). "Próprio" limita aos dados do profissional logado; "Toda a entidade" mostra tudo (recepção/secretaria).
                </Typography>

                {/*
                  Liberacao de dado clinico (RN-555). E a "configuracao explicita da entidade" que o
                  item 4 do termo de uso ja promete. Deny-by-default: sem isto, ferramenta clinica
                  nao e oferecida ao agente, por mais permissao que o perfil tenha.
                */}
                <Box
                  sx={{
                    border: `1px solid ${form.allowsClinicalData ? colors.primary : "#e9ecef"}`,
                    borderRadius: "8px",
                    p: "0.75rem 1rem",
                    backgroundColor: form.allowsClinicalData ? "#e8f7f9" : "transparent",
                  }}
                >
                  <FormControlLabel
                    control={
                      <Switch
                        checked={!!form.allowsClinicalData}
                        onChange={(e) => patch({ allowsClinicalData: e.target.checked })}
                        sx={{ "& .Mui-checked": { color: colors.primary } }}
                      />
                    }
                    label={
                      <Typography sx={{ fontSize: 14, fontWeight: 600 }}>
                        Liberar dado clínico para este agente
                      </Typography>
                    }
                  />
                  <Typography variant="caption" sx={{ display: "block", color: colors.textSecondary }}>
                    Desligado, o agente nunca recebe plano terapêutico, evolução, anotação, diagnóstico
                    nem avaliação — mesmo que o perfil-alvo acesse essas telas. Ligado, ele passa a
                    consultar esses dados <strong>com a mesma régua do profissional logado</strong>: o
                    que é dele, ele vê; o que é de outro, não. O conteúdo trafega até o provedor de IA
                    escolhido, e é por isso que a liberação é explícita e sua — o termo de uso aceito
                    pela entidade responsabiliza a clínica por ela.
                  </Typography>
                </Box>

                <Divider sx={{ my: 0.5 }} />

                <Typography variant="caption" sx={{ color: colors.textSecondary }}>
                  O perfil-alvo define o que o agente <strong>poderia</strong> usar; marque o que ele usa.
                  Ferramenta nova do sistema chega <strong>desligada</strong> em todos os agentes: ligue aqui
                  onde ela fizer sentido. Ligar nunca passa do que o perfil-alvo acessa.
                </Typography>

                {!form.id ? (
                  <Box sx={{ textAlign: "center", py: 4, color: colors.textSecondary, border: "1px dashed #ced4da", borderRadius: "8px" }}>
                    <Typography variant="body2">Salve o agente para definir as ferramentas.</Typography>
                  </Box>
                ) : loadingTools || agentTools === null ? (
                  <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
                    <CircularProgress size={24} sx={{ color: colors.primary }} />
                  </Box>
                ) : (() => {
                  // O perfil-alvo define o TETO (o agente nunca ultrapassa); dentro dele, cada
                  // ferramenta liga e desliga neste agente (RN-556). Clínico-sensível só aparece
                  // com a liberação da RN-555 — sem ela nem chega ao modelo.
                  const included = agentTools.filter(
                    (t) => t.available && (t.sensitivity !== "clinico_sensivel" || form.allowsClinicalData)
                  );
                  return included.length === 0 ? (
                    <Box sx={{ textAlign: "center", py: 3, color: colors.textSecondary, border: "1px dashed #ced4da", borderRadius: "8px" }}>
                      <Typography variant="body2">
                        {form.targetProfileId
                          ? "Este perfil não acessa nenhuma ferramenta de dados."
                          : "Selecione um perfil-alvo para ver as ferramentas."}
                      </Typography>
                    </Box>
                  ) : (
                    <AgentToolsPicker
                      key={form.id}
                      tools={included}
                      enabled={form.enabledTools}
                      onChange={(enabledTools) => patch({ enabledTools })}
                      initialKind={form.toolKind}
                    />
                  );
                })()}
              </Box>

              {/* Aba Modelo (override de provedor/modelo por agente — RN-511) */}
              <Box sx={{ display: tab === 5 ? "flex" : "none", flexDirection: "column", gap: 2 }}>
                <FormControlLabel
                  control={<Checkbox checked={form.customizeProvider} onChange={(e) => patch({ customizeProvider: e.target.checked })} sx={{ color: colors.primary, "&.Mui-checked": { color: colors.primary } }} />}
                  label="Personalizar provedor/modelo para este agente"
                />
                <Typography variant="caption" sx={{ display: "block", color: colors.textSecondary, mt: -1 }}>
                  Sem personalizar, o agente usa a Configuração de IA da entidade (provedor e modelo padrão).
                </Typography>
                <Collapse in={form.customizeProvider}>
                  <Box sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 1 }}>
                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2 }}>
                      <FormControl size="small" sx={{ minWidth: 200, flex: "1 1 200px" }}>
                        <InputLabel>Provedor</InputLabel>
                        <Select label="Provedor" value={form.integrationId} onChange={(e) => changeProvider(e.target.value)}>
                          {options.map((o) => (<MenuItem key={o.id} value={o.id}>{o.displayName}</MenuItem>))}
                        </Select>
                      </FormControl>
                      <FormControl size="small" sx={{ minWidth: 220, flex: "1 1 220px" }}>
                        <InputLabel>Modelo</InputLabel>
                        <Select label="Modelo" value={form.model} onChange={(e) => patch({ model: e.target.value })}>
                          {(selectedProvider?.models || []).map((m) => (<MenuItem key={m} value={m}>{modelLabel(m)}</MenuItem>))}
                        </Select>
                      </FormControl>
                    </Box>
                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
                      <Box sx={{ flex: "1 1 200px", minWidth: 180 }}>
                        <Typography variant="body2" sx={{ color: colors.textPrimary, mb: 0.5 }}>Esforço de raciocínio</Typography>
                        <ToggleButtonGroup exclusive size="small" value={form.reasoningEffort} onChange={(_, v) => v && patch({ reasoningEffort: v })}>
                          {EFFORTS.map((e) => (<ToggleButton key={e.value} value={e.value} sx={{ textTransform: "none" }}>{e.label}</ToggleButton>))}
                        </ToggleButtonGroup>
                      </Box>
                    </Box>
                  </Box>
                </Collapse>
              </Box>
            </>
          )}
        </DialogContent>
        <DialogActions sx={{ padding: "1rem 1.25rem" }}>
          <Button onClick={closeForm} variant="outlined" sx={{ textTransform: "none", color: "#6c757d", borderColor: "#6c757d" }}>Cancelar</Button>
          <Button onClick={save} variant="contained" disabled={saving} sx={{ textTransform: "none", backgroundColor: colors.primary }}>
            {saving ? "Salvando..." : form?.id ? "Salvar" : "Criar agente"}
          </Button>
        </DialogActions>
      </Dialog>

      <ConfirmModal
        isOpen={deleteTarget !== null}
        title="Excluir agente"
        message={`Excluir o agente "${deleteTarget?.displayName ?? ""}"? Ele sai do cadastro (o registro é preservado para auditoria).`}
        type="warning"
        confirmButtonText="Excluir"
        cancelButtonText="Cancelar"
        isProcessing={saving}
        onConfirm={confirmDelete}
        onCancel={() => setDeleteTarget(null)}
      />

      <FooterInternal className="login-footer-component" />
      <Toast message={toast.message} type={toast.type} isVisible={toast.isVisible} onClose={hideToast} />
    </Box>
  );
};

export default AiAgents;
